import { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";

const formSchema = z.object({
  businessName: z.string().min(2, "Business name is required"),
  website: z.string().min(1, "Website is required"),
  contactName: z.string().min(2, "Contact name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  currentTraffic: z.string(),
  competitors: z.string(),
  goals: z.string(),
  targetAudience: z.string(),
  additionalInfo: z.string(),
});

type OnboardingFormProps = {
  selectedPlan: string;
  onSubmit: (data: z.infer<typeof formSchema>) => void;
  onClose: () => void;
};

const steps = [
  { id: "business", label: "Business Info" },
  { id: "contact", label: "Contact Details" },
  { id: "seo", label: "SEO Profile" },
  { id: "goals", label: "Goals & Audience" },
  { id: "review", label: "Review" },
];

const SeoOnboardingForm = ({ selectedPlan, onSubmit, onClose }: OnboardingFormProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [savedValues, setSavedValues] = useState<Partial<z.infer<typeof formSchema>>>({});
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      businessName: savedValues.businessName || "",
      website: savedValues.website || "",
      contactName: savedValues.contactName || "",
      email: savedValues.email || "",
      phone: savedValues.phone || "",
      currentTraffic: savedValues.currentTraffic || "",
      competitors: savedValues.competitors || "",
      goals: savedValues.goals || "",
      targetAudience: savedValues.targetAudience || "",
      additionalInfo: savedValues.additionalInfo || "",
    },
  });

  const progress = ((currentStep + 1) / steps.length) * 100;

  const saveAndContinue = () => {
    const currentValues = form.getValues();
    
    setSavedValues({ ...savedValues, ...currentValues });
    
    localStorage.setItem("seoOnboardingData", JSON.stringify({ 
      ...savedValues,
      ...currentValues,
      selectedPlan,
      lastStep: currentStep + 1,
      timestamp: new Date().toISOString()
    }));
    
    setCurrentStep(current => current + 1);
  };

  const goBack = () => {
    setCurrentStep(current => current - 1);
  };

  const handleFinalSubmit = () => {
    const formData = form.getValues();
    localStorage.removeItem("seoOnboardingData");
    onSubmit(formData);
  };

  const validateAndProceed = async () => {
    let fieldsToValidate: (keyof z.infer<typeof formSchema>)[] = [];
    
    switch (currentStep) {
      case 0: // Business Info
        fieldsToValidate = ['businessName', 'website'];
        break;
      case 1: // Contact Details
        fieldsToValidate = ['contactName', 'email', 'phone'];
        break;
      case 2: // SEO Profile
        fieldsToValidate = ['currentTraffic', 'competitors'];
        break;
      case 3: // Goals & Audience
        fieldsToValidate = ['goals', 'targetAudience'];
        break;
      default:
        break;
    }
    
    const result = await form.trigger(fieldsToValidate as any);
    if (result) {
      saveAndContinue();
    }
  };

  return (
    <Form {...form}>
      <div className="space-y-6">
        <div>
          <h2 className="text-xl font-bold">
            {currentStep < steps.length - 1 ? steps[currentStep].label : "Review Your Application"}
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            {currentStep === 0 && "Tell us about your business"}
            {currentStep === 1 && "How can we reach you?"}
            {currentStep === 2 && "Let's understand your current SEO situation"}
            {currentStep === 3 && "What are you looking to achieve?"}
            {currentStep === 4 && "Please review your information before submitting"}
          </p>
        </div>

        <div className="relative">
          <Progress value={progress} className="h-2" />
          <div className="flex justify-between mt-2 text-xs">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex flex-col items-center ${index <= currentStep ? 'text-primary' : 'text-muted-foreground'}`}
                style={{ width: `${100 / steps.length}%` }}
              >
                <div className={`w-4 h-4 rounded-full flex items-center justify-center mb-1 ${
                  index < currentStep ? 'bg-primary text-white' : 
                  index === currentStep ? 'border-2 border-primary' : 'border-2 border-muted'
                }`}>
                  {index < currentStep && <CheckCircle className="w-3 h-3" />}
                </div>
                <span className="hidden sm:inline text-center">{step.label}</span>
              </div>
            ))}
          </div>
        </div>

        <Tabs value={currentStep.toString()} className="mt-8">
          <TabsContent value="0" className="m-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="businessName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your Business Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="website"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Website</FormLabel>
                          <FormControl>
                            <Input placeholder="yourwebsite.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          <TabsContent value="1" className="m-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="contactName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Contact Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your Name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="your@email.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone</FormLabel>
                          <FormControl>
                            <Input placeholder="(555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          <TabsContent value="2" className="m-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 gap-6">
                    <FormField
                      control={form.control}
                      name="currentTraffic"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Monthly Traffic</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select traffic range" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="0-1000">0 - 1,000</SelectItem>
                              <SelectItem value="1000-5000">1,000 - 5,000</SelectItem>
                              <SelectItem value="5000-10000">5,000 - 10,000</SelectItem>
                              <SelectItem value="10000-50000">10,000 - 50,000</SelectItem>
                              <SelectItem value="50000+">50,000+</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="competitors"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Main Competitors (URLs)</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="List your main competitors' websites"
                              className="min-h-[80px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          <TabsContent value="3" className="m-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-1 gap-6">
                    <FormField
                      control={form.control}
                      name="goals"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>SEO Goals</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="What are your main SEO objectives?"
                              className="min-h-[80px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="targetAudience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target Audience</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Describe your ideal customers"
                              className="min-h-[80px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="additionalInfo"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Additional Information</FormLabel>
                          <FormControl>
                            <Textarea 
                              placeholder="Any other details you'd like to share?"
                              className="min-h-[80px]"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          <TabsContent value="4" className="m-0">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-medium text-lg mb-4">Application Summary</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">Business Information</h4>
                      <p className="mt-1"><span className="font-medium">Business:</span> {form.getValues().businessName}</p>
                      <p className="mt-1"><span className="font-medium">Website:</span> {form.getValues().website}</p>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground">Contact Details</h4>
                      <p className="mt-1"><span className="font-medium">Name:</span> {form.getValues().contactName}</p>
                      <p className="mt-1"><span className="font-medium">Email:</span> {form.getValues().email}</p>
                      <p className="mt-1"><span className="font-medium">Phone:</span> {form.getValues().phone}</p>
                    </div>
                    
                    <div className="md:col-span-2">
                      <h4 className="text-sm font-medium text-muted-foreground">SEO Details</h4>
                      <p className="mt-1"><span className="font-medium">Monthly Traffic:</span> {form.getValues().currentTraffic}</p>
                      <p className="mt-1"><span className="font-medium">Selected Plan:</span> {selectedPlan}</p>
                    </div>
                    
                    <div className="md:col-span-2">
                      <p className="text-muted-foreground text-sm mt-4">
                        By submitting this form, you agree to our Terms of Service and Privacy Policy.
                        We'll contact you within 24 hours to discuss next steps.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-between">
          {currentStep > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={goBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          
          {currentStep === 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="mr-auto"
            >
              Cancel
            </Button>
          )}
          
          {currentStep < steps.length - 1 ? (
            <Button
              type="button"
              onClick={validateAndProceed}
              className="ml-auto flex items-center gap-2"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleFinalSubmit}
              className="ml-auto bg-primary-gradient"
            >
              Submit Application
            </Button>
          )}
        </div>
      </div>
    </Form>
  );
};

export default SeoOnboardingForm;
